import React, {useEffect, useRef, useState} from 'react';
import {
  ActivityIndicator,
  Modal,
  Pressable,
  ScrollView,
  View,
  useWindowDimensions,
} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';
import {
  CreateErrorView,
  createBlockedReason,
  createLabel,
  describeCreateError,
  droppedReasons,
  humanServerText,
  LaunchedPuzzle,
  reasonText,
  sanLine,
  summaryLine,
  thinkingTime,
  useActivePuzzles,
  useCreateActivePuzzleCourse,
} from '../../../Hooks/LmsCourseApi/useActivePuzzles';
import {
  courseNote,
  Masterclass,
} from '../../../Hooks/LmsCourseApi/useMasterclasses';
import {CourseJob} from '../../../Hooks/LmsCourseApi/types';

import CourseButton from './CourseButton';
import MiniBoard from './MiniBoard';
import PuzzleIcon from './PuzzleIcon';
import {formatDuration, formatTimestamp} from '../courseFormat';
import {F, S, STATUS, T} from '../courseTheme';
import {E} from '../editor/editorTheme';
import {focusVisible, motion, ring} from '../editor/ui';
import {prefersReducedMotion} from './prefersReducedMotion';

type Props = {
  /** The picker row that was chosen; its title stands in while loading. */
  item: Masterclass;
  onClose: () => void;
  /** The create call was accepted: follow this job like any other build. */
  onStarted: (job: CourseJob) => void;
};

/** Below this panel width the boards shrink, so a phone still shows several rows. */
const NARROW_PANEL = 520;

/** A footer button may shrink to the sheet's width, never past it. */
const FIT = {maxWidth: '100%', flexShrink: 1} as const;

const sideWord = (side: string) => (side === 'black' ? 'Black' : 'White');

/** "22 Sep 2026" from the recording's ISO start; '' when there is none. */
function recordingDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Icon + word, so a status never rests on colour alone. */
function Mark({
  icon,
  word,
  color,
  label,
}: {
  icon: string;
  word: string;
  color: string;
  label?: string;
}) {
  return (
    <View
      accessible
      accessibilityLabel={label ?? word}
      style={{flexDirection: 'row', alignItems: 'center', gap: 4}}>
      <Ionicons name={icon} size={16} color={color} />
      <CCText style={{fontSize: T.sm, fontWeight: '600', color}}>{word}</CCText>
    </View>
  );
}

function PuzzleRow({
  puzzle,
  first,
  boardSize,
}: {
  puzzle: LaunchedPuzzle;
  first: boolean;
  boardSize: number;
}) {
  const dropped = puzzle.status === 'dropped';
  const side = sideWord(puzzle.side);
  const at = puzzle.launched_at || formatTimestamp(puzzle.launched_at_sec);
  // A line that does not replay has no SAN, but the coach did store moves:
  // show them as stored rather than claiming there are none.
  const line =
    sanLine(puzzle.moves_san ?? [], puzzle.fen) ||
    (puzzle.moves_uci ?? []).join(' ');
  const time = thinkingTime(puzzle.time_limit_sec);
  // Text on a left-out row stays at mutedText (5.3:1 on white), not faded
  // with opacity: quieter, still readable.
  const ink = dropped ? F.mutedText : F.brownDark;
  const body = dropped ? F.mutedText : F.brown;

  return (
    <View
      style={{
        flexDirection: 'row',
        gap: boardSize < 128 ? S[3] : S[4],
        paddingVertical: S[4],
        borderTopWidth: first ? 0 : 1,
        borderTopColor: F.border10,
      }}>
      <MiniBoard
        fen={puzzle.fen}
        // The solver's side at the bottom, the way the course will show it.
        orientation={puzzle.side === 'black' ? 'black' : 'white'}
        size={boardSize}
        label={`Position at ${at}, ${side} to move`}
        muted={dropped}
      />

      <View style={{flex: 1, minWidth: 0, gap: S[2]}}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: S[3],
          }}>
          <CCText
            accessibilityLabel={`Launched at ${at}`}
            style={
              {
                fontSize: T.lg,
                fontWeight: '700',
                color: ink,
                fontVariant: ['tabular-nums'],
              } as any
            }>
            {at}
          </CCText>
          {dropped ? (
            <Mark
              icon="remove-circle-outline"
              word="Left out"
              color={STATUS.unknown.fg}
            />
          ) : (
            <Mark
              icon={STATUS.good.icon}
              word="Will be used"
              color={STATUS.good.fg}
            />
          )}
        </View>

        {puzzle.name ? (
          <CCText style={{fontSize: T.base, fontWeight: '600', color: ink}}>
            {puzzle.name}
          </CCText>
        ) : null}

        <View style={{flexDirection: 'row', flexWrap: 'wrap', gap: S[3]}}>
          {/* Hollow for White, filled for Black: the shape says it too. */}
          <Mark
            icon={puzzle.side === 'black' ? 'ellipse' : 'ellipse-outline'}
            word={`${side} to move`}
            color={body}
          />
          {time ? (
            <Mark
              icon="time-outline"
              word={time}
              color={body}
              label={`Thinking time ${time}`}
            />
          ) : null}
          {puzzle.mystery_bonus ? (
            <Mark icon="gift-outline" word="Mystery bonus" color={F.tealDark} />
          ) : null}
        </View>

        <View style={{gap: 2}}>
          <CCText
            style={{fontSize: T.xs, fontWeight: '600', color: F.mutedText}}>
            {"Coach's line"}
          </CCText>
          {line ? (
            <CCText
              style={{
                fontSize: T.base,
                fontWeight: '600',
                color: body,
                lineHeight: 22,
                // Struck, but in the same readable colour: a reader sees
                // "not used" from the line and the word, not from a fade.
                textDecorationLine: dropped ? 'line-through' : 'none',
              }}>
              {line}
            </CCText>
          ) : (
            <CCText
              style={{fontSize: T.sm, fontWeight: '500', color: F.mutedText}}>
              No moves stored
            </CCText>
          )}
        </View>

        {dropped && puzzle.reason ? (
          <View
            style={{flexDirection: 'row', alignItems: 'flex-start', gap: 6}}>
            <Ionicons
              name="information-circle-outline"
              size={16}
              color={STATUS.unknown.fg}
              style={{marginTop: 2}}
            />
            <CCText
              style={{
                flex: 1,
                fontSize: T.sm,
                fontWeight: '600',
                color: STATUS.unknown.fg,
                lineHeight: 20,
              }}>
              {reasonText(puzzle.reason)}
            </CCText>
          </View>
        ) : null}

        {puzzle.warning ? (
          <View
            style={{flexDirection: 'row', alignItems: 'flex-start', gap: 6}}>
            <Ionicons
              name={STATUS.warn.icon}
              size={16}
              color={STATUS.warn.fg}
              style={{marginTop: 2}}
            />
            <CCText
              style={{
                flex: 1,
                fontSize: T.sm,
                fontWeight: '600',
                color: STATUS.warn.fg,
                lineHeight: 20,
              }}>
              {puzzle.warning}
            </CCText>
          </View>
        ) : null}
      </View>
    </View>
  );
}

/**
 * Every puzzle a coach launched in one recording, before a course is built
 * from them.
 *
 * The admin asked to be told, before creating, how many puzzles there are
 * and all the details — so this lists every launch, kept or not, and the one
 * primary action names exactly how many puzzles it will use. Left-out
 * launches stay in the list with their reason in words: a recording that
 * looks short of puzzles is explained rather than mysterious.
 *
 * A react-native Modal, i.e. a portal to <body> on the web. Not an overlay
 * inside the picker: react-native-web's ScrollView carries a transform, which
 * makes `position: fixed` fixed to the scroll view rather than the window, and
 * the sheet came out clipped top and bottom. The Modal brings role=dialog,
 * aria-modal, a focus trap and Escape; focus goes back to the tile on close.
 */
export default function ActivePuzzlePreview({item, onClose, onStarted}: Props) {
  const report = useActivePuzzles(item.video_course_id);
  const create = useCreateActivePuzzleCourse();
  const [failure, setFailure] = useState<CreateErrorView | null>(null);
  const [closeFocused, setCloseFocused] = useState(false);
  const [reduced] = useState(prefersReducedMotion);
  const {width, height} = useWindowDimensions();
  const closeRef = useRef<any>(null);

  // Focus in on open, and back to whatever opened the sheet on close.
  useEffect(() => {
    const doc = (globalThis as any).document;
    const opener = doc?.activeElement ?? null;
    closeRef.current?.focus?.();
    return () => {
      if (opener && doc?.contains?.(opener)) opener.focus?.();
    };
  }, []);

  const data = report.data;
  const recording = data?.recording;
  const summary = data?.summary;
  const puzzles = data?.puzzles ?? [];
  const kept = summary?.kept ?? 0;
  const blocked = summary ? createBlockedReason(kept) : null;
  const empty = !!data && puzzles.length === 0;

  const panelWidth = Math.max(280, Math.min(760, width - 32));
  const boardSize = panelWidth < NARROW_PANEL ? 104 : 128;

  const title = recording?.title || item.title;
  const meta = [
    recording?.coach || item.coach,
    recordingDate(recording?.recording_start),
    formatDuration(recording?.duration_sec ?? (item.duration_min || 0) * 60),
    recording?.category || item.class_name || item.category,
  ]
    .filter(Boolean)
    .join(' · ');

  const start = async (force: boolean) => {
    setFailure(null);
    try {
      const job = await create.mutateAsync(
        force
          ? {video_course_id: item.video_course_id, force: true}
          : {video_course_id: item.video_course_id},
      );
      onStarted(job);
    } catch (error) {
      setFailure(describeCreateError(error));
    }
  };

  const reasons = summary ? droppedReasons(summary) : [];
  const existingNote = courseNote(item);
  const conflict = failure?.kind === 'exists';

  const createDisabled = !data || empty || blocked != null || create.isLoading;
  const createText = create.isLoading
    ? 'Starting…'
    : data
    ? createLabel(kept)
    : 'Create course';

  return (
    <Modal
      visible
      transparent
      animationType={reduced ? 'none' : 'fade'}
      onRequestClose={onClose}
      {...({'aria-label': `Active puzzles in ${title}`} as any)}>
      <View
        style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          padding: S[4],
        }}>
        {/* Clicking away closes, which is what everyone tries first. Not a
          second "Close" for screen readers: they have the labelled button
          and Escape. */}
        <Pressable
          accessible={false}
          focusable={false}
          onPress={onClose}
          style={
            {
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: E.scrim,
              cursor: 'default',
            } as any
          }
        />

        <View
          style={{
            width: panelWidth,
            maxHeight: Math.max(320, height - 32),
            backgroundColor: F.white,
            borderRadius: F.radius.card,
            overflow: 'hidden',
            shadowColor: '#000',
            shadowOffset: {width: 0, height: 12},
            shadowOpacity: 0.18,
            shadowRadius: 32,
            elevation: 12,
          }}>
          {/* --- header: always visible --- */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: S[3],
              paddingLeft: S[5],
              paddingRight: S[2],
              paddingTop: S[3],
              paddingBottom: S[3],
              borderBottomWidth: 1,
              borderBottomColor: F.border10,
            }}>
            <View style={{flex: 1, minWidth: 0, gap: 2, paddingTop: S[1]}}>
              <View
                style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
                <PuzzleIcon size={16} color={F.tealDark} />
                <CCText
                  style={{
                    fontSize: T.xs,
                    fontWeight: '700',
                    color: F.tealDark,
                  }}>
                  ACTIVE PUZZLES
                </CCText>
              </View>
              <CCText
                accessibilityRole="header"
                style={{fontSize: T.xl, fontWeight: '700', color: F.brownDark}}>
                {title}
              </CCText>
              {meta ? (
                <CCText
                  style={{fontSize: T.sm, fontWeight: '500', color: F.brown}}>
                  {meta}
                </CCText>
              ) : null}
            </View>

            <Pressable
              ref={closeRef}
              accessibilityRole="button"
              accessibilityLabel="Close active puzzles"
              onPress={onClose}
              onFocus={() => setCloseFocused(focusVisible())}
              onBlur={() => setCloseFocused(false)}
              style={({pressed, hovered}: any) => [
                {
                  width: 44,
                  height: 44,
                  borderRadius: 22,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: pressed
                    ? E.pressed
                    : hovered
                    ? E.hover
                    : 'transparent',
                  cursor: 'pointer',
                } as any,
                motion(reduced),
                ring(closeFocused),
              ]}>
              <Ionicons name="close" size={22} color={F.brownDark} />
            </Pressable>
          </View>

          {/* --- body: the one scrolling region --- */}
          <ScrollView
            style={{flexGrow: 0, flexShrink: 1}}
            contentContainerStyle={{
              paddingHorizontal: S[5],
              paddingVertical: S[4],
              gap: S[3],
            }}>
            {report.isLoading ? (
              <View
                accessibilityLiveRegion="polite"
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: S[3],
                  paddingVertical: S[6],
                }}>
                <ActivityIndicator color={F.tealDark} />
                <CCText
                  style={{fontSize: T.base, fontWeight: '500', color: F.brown}}>
                  Reading the puzzles launched in this class…
                </CCText>
              </View>
            ) : null}

            {report.isError ? (
              <View style={{gap: S[3], paddingVertical: S[4]}}>
                <View
                  accessibilityLiveRegion="polite"
                  style={{
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    gap: S[2],
                  }}>
                  <Ionicons
                    name={STATUS.bad.icon}
                    size={18}
                    color={STATUS.bad.fg}
                  />
                  <CCText
                    style={{
                      flex: 1,
                      fontSize: T.base,
                      fontWeight: '600',
                      color: STATUS.bad.fg,
                      lineHeight: 22,
                    }}>
                    {humanServerText(
                      (report.error as any)?.response?.data?.error,
                    ) ?? 'Could not read the puzzles for this recording.'}
                  </CCText>
                </View>
                <View style={{alignSelf: 'flex-start'}}>
                  <CourseButton
                    label="Try again"
                    variant="review"
                    onPress={() => report.refetch()}
                  />
                </View>
              </View>
            ) : null}

            {empty ? (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: S[2],
                  paddingVertical: S[4],
                }}>
                <Ionicons
                  name="information-circle-outline"
                  size={18}
                  color={F.brown}
                />
                <CCText
                  style={{
                    flex: 1,
                    fontSize: T.base,
                    fontWeight: '500',
                    color: F.brown,
                    lineHeight: 22,
                  }}>
                  No puzzles were launched during this recording, so there is
                  nothing to build a course from. Pick another recording.
                </CCText>
              </View>
            ) : null}

            {summary && !empty ? (
              <View style={{gap: S[2]}}>
                <CCText
                  accessibilityRole="header"
                  style={{
                    fontSize: T.lg,
                    fontWeight: '700',
                    color: F.brownDark,
                  }}>
                  {summaryLine(summary)}
                </CCText>
                {reasons.length ? (
                  <View style={{gap: 4}}>
                    <CCText
                      style={{
                        fontSize: T.sm,
                        fontWeight: '600',
                        color: F.brown,
                      }}>
                      Left out because:
                    </CCText>
                    {reasons.map(row => (
                      <View
                        key={row.reason}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'flex-start',
                          gap: 6,
                        }}>
                        <Ionicons
                          name="remove-circle-outline"
                          size={16}
                          color={STATUS.unknown.fg}
                          style={{marginTop: 2}}
                        />
                        <CCText
                          style={{
                            flex: 1,
                            fontSize: T.sm,
                            fontWeight: '500',
                            color: F.brown,
                            lineHeight: 20,
                          }}>
                          {`${row.reason}: ${row.count}`}
                        </CCText>
                      </View>
                    ))}
                  </View>
                ) : null}
              </View>
            ) : null}

            {puzzles.length ? (
              <View>
                {puzzles.map((puzzle, index) => (
                  <PuzzleRow
                    key={puzzle.id}
                    puzzle={puzzle}
                    first={index === 0}
                    boardSize={boardSize}
                  />
                ))}
              </View>
            ) : null}
          </ScrollView>

          {/* --- footer: the action, always in reach --- */}
          <View
            style={{
              gap: S[3],
              paddingHorizontal: S[5],
              paddingVertical: S[4],
              borderTopWidth: 1,
              borderTopColor: F.border10,
              backgroundColor: F.white,
            }}>
            {existingNote && !failure ? (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: 6,
                }}>
                <Ionicons
                  name="information-circle-outline"
                  size={16}
                  color={F.brown}
                  style={{marginTop: 2}}
                />
                <CCText
                  style={{
                    flex: 1,
                    fontSize: T.sm,
                    fontWeight: '500',
                    color: F.brown,
                    lineHeight: 20,
                  }}>
                  {`${existingNote}. Creating will ask before replacing it.`}
                </CCText>
              </View>
            ) : null}

            {blocked && !empty ? (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: 6,
                }}>
                <Ionicons
                  name={STATUS.warn.icon}
                  size={16}
                  color={STATUS.warn.fg}
                  style={{marginTop: 2}}
                />
                <CCText
                  style={{
                    flex: 1,
                    fontSize: T.sm,
                    fontWeight: '600',
                    color: STATUS.warn.fg,
                    lineHeight: 20,
                  }}>
                  {blocked}
                </CCText>
              </View>
            ) : null}

            {failure ? (
              <View
                accessibilityLiveRegion="polite"
                style={{
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: 6,
                }}>
                <Ionicons
                  name={conflict ? STATUS.warn.icon : STATUS.bad.icon}
                  size={16}
                  color={conflict ? STATUS.warn.fg : STATUS.bad.fg}
                  style={{marginTop: 2}}
                />
                <View style={{flex: 1, gap: 2}}>
                  <CCText
                    style={{
                      fontSize: T.sm,
                      fontWeight: '600',
                      color: conflict ? STATUS.warn.fg : STATUS.bad.fg,
                      lineHeight: 20,
                    }}>
                    {failure.message}
                  </CCText>
                  {conflict ? (
                    <CCText
                      style={{
                        fontSize: T.sm,
                        fontWeight: '500',
                        color: F.brown,
                        lineHeight: 20,
                      }}>
                      {`Rebuilding replaces it with a new course made from these ${createLabel(
                        kept,
                      ).replace(
                        'Create course from ',
                        '',
                      )}. Keep it if you only wanted to look.`}
                    </CCText>
                  ) : null}
                </View>
              </View>
            ) : null}

            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: S[3],
                alignItems: 'center',
              }}>
              {/* Wrapped so a long label wraps inside the sheet on a phone
                instead of running off its edge. */}
              {conflict ? (
                <>
                  <View style={FIT}>
                    <CourseButton
                      label={create.isLoading ? 'Starting…' : 'Rebuild course'}
                      disabled={create.isLoading}
                      onPress={() => start(true)}
                    />
                  </View>
                  <View style={FIT}>
                    <CourseButton
                      label="Keep the existing course"
                      variant="review"
                      disabled={create.isLoading}
                      onPress={() => setFailure(null)}
                    />
                  </View>
                </>
              ) : (
                <>
                  <View style={FIT}>
                    <CourseButton
                      label={createText}
                      disabled={createDisabled}
                      onPress={() => start(false)}
                    />
                  </View>
                  <View style={FIT}>
                    <CourseButton
                      label="Cancel"
                      variant="review"
                      onPress={onClose}
                    />
                  </View>
                </>
              )}
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
}
